import { createFileRoute, Link, Outlet } from "@tanstack/react-router";
import { MoonIcon, SunIcon } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import logoMark from "@/assets/landing/logo-rocortado.png";
import { useLangChange } from "@/hooks/use-lang-change";
import { languages } from "@/i18n";
import { useTheme } from "@/providers/theme-provider";

function Logo({ className }: { className?: string }) {
	return (
		<span
			className={`flex items-center gap-2.5 font-bold text-[17px] text-brand-brown tracking-tight dark:text-stone-100 ${className ?? ""}`}
		>
			<img alt="" className="h-9 w-auto" height={243} src={logoMark} width={565} />
			OdontOffice
		</span>
	);
}

/** Compact segmented language switch (es | en) for the marketing chrome. */
function LangToggle() {
	const { t } = useTranslation();
	const { currentLocale, setLang } = useLangChange();

	return (
		<fieldset
			aria-label={t(($) => $.modules.marketing.langToggle.label)}
			className="flex items-center gap-0.5 rounded-full border border-stone-200 bg-white/60 p-0.5 backdrop-blur-md dark:border-white/10 dark:bg-white/4"
		>
			{languages.map((lang) => {
				const active = currentLocale === lang;
				return (
					<button
						aria-pressed={active}
						className={`rounded-full px-2.5 py-1 font-semibold text-[12.5px] uppercase transition-colors ${
							active
								? "bg-brand-teal text-white"
								: "text-stone-500 hover:text-brand-brown dark:text-stone-400 dark:hover:text-stone-100"
						}`}
						key={lang}
						onClick={() => setLang(lang)}
						type="button"
					>
						{lang}
					</button>
				);
			})}
		</fieldset>
	);
}

/** Light/dark toggle. Tracks the resolved theme via the `dark` class so it
 *  stays correct under the provider's "system" mode too. */
function ThemeToggle() {
	const { t } = useTranslation();
	const { setTheme } = useTheme();
	const [isDark, setIsDark] = useState(() =>
		typeof document !== "undefined" ? document.documentElement.classList.contains("dark") : false
	);

	useEffect(() => {
		const root = document.documentElement;
		const sync = () => setIsDark(root.classList.contains("dark"));
		sync();
		const observer = new MutationObserver(sync);
		observer.observe(root, { attributes: true, attributeFilter: ["class"] });
		return () => observer.disconnect();
	}, []);

	return (
		<button
			aria-label={
				isDark ? t(($) => $.modules.marketing.themeToggle.toLight) : t(($) => $.modules.marketing.themeToggle.toDark)
			}
			className="grid size-9 place-items-center rounded-full border border-stone-200 bg-white/60 text-stone-500 backdrop-blur-md transition-colors hover:text-brand-brown dark:border-white/10 dark:bg-white/4 dark:text-stone-400 dark:hover:text-stone-100"
			onClick={() => setTheme(isDark ? "light" : "dark")}
			type="button"
		>
			{isDark ? <SunIcon className="size-4.5" /> : <MoonIcon className="size-4.5" />}
		</button>
	);
}

export const Route = createFileRoute("/(marketing)")({
	component: MarketingLayout,
});

function MarketingLayout() {
	const { t } = useTranslation();

	return (
		<div className="flex min-h-dvh flex-col bg-[#fdfbf6] text-brand-brown dark:bg-[#14100b] dark:text-stone-100">
			<header className="fixed top-0 z-50 w-full border-stone-200/70 border-b bg-[#fdfbf6]/70 backdrop-blur-xl backdrop-saturate-150 dark:border-white/10 dark:bg-[#14100b]/70">
				<div className="mx-auto flex h-16 max-w-[1160px] items-center gap-7 px-6">
					<Link to="/">
						<Logo />
					</Link>
					<nav className="ml-2 hidden items-center gap-6 font-medium text-[14.5px] text-stone-500 md:flex dark:text-stone-400">
						<Link
							className="transition-colors hover:text-brand-brown dark:hover:text-stone-100"
							hash="caracteristicas"
							to="/"
						>
							{t(($) => $.modules.marketing.nav.product)}
						</Link>
						<Link
							className="transition-colors hover:text-brand-brown dark:hover:text-stone-100"
							hash="odontograma"
							to="/"
						>
							{t(($) => $.modules.marketing.nav.odontogram)}
						</Link>
						<Link className="transition-colors hover:text-brand-brown dark:hover:text-stone-100" hash="precios" to="/">
							{t(($) => $.modules.marketing.nav.pricing)}
						</Link>
					</nav>
					<div className="ml-auto flex items-center gap-3">
						<LangToggle />
						<ThemeToggle />
						<Link
							className="hidden font-semibold text-[14.5px] text-stone-500 transition-colors hover:text-brand-brown sm:block dark:text-stone-400 dark:hover:text-stone-100"
							to="/auth/login"
						>
							{t(($) => $.modules.marketing.nav.login)}
						</Link>
						<Link
							className="inline-flex h-10 items-center gap-2 rounded-full bg-linear-to-b from-[#3fc6d2] to-brand-teal px-5 font-semibold text-[14.5px] text-white shadow-[0_8px_22px_-6px_rgba(47,180,192,0.5),inset_0_1px_0_rgba(255,255,255,0.35)] transition-transform hover:-translate-y-0.5"
							to="/auth/register"
						>
							{t(($) => $.modules.marketing.nav.freeTrial)}
						</Link>
					</div>
				</div>
			</header>

			<main className="flex-1">
				<Outlet />
			</main>

			<footer className="border-stone-200 border-t bg-[#fdfbf6] dark:border-white/10 dark:bg-[#14100b]">
				<div className="mx-auto max-w-[1160px] px-6 py-14 text-[14px] text-stone-500 dark:text-stone-400">
					<div className="mb-9 flex flex-wrap justify-between gap-8">
						<div className="max-w-[260px]">
							<Logo className="mb-3" />
							<p className="m-0">{t(($) => $.modules.marketing.footer.tagline)}</p>
						</div>
						<FooterCol
							links={[
								t(($) => $.modules.marketing.footer.columns.product.links.l1),
								t(($) => $.modules.marketing.footer.columns.product.links.l2),
								t(($) => $.modules.marketing.footer.columns.product.links.l3),
								t(($) => $.modules.marketing.footer.columns.product.links.l4),
							]}
							title={t(($) => $.modules.marketing.footer.columns.product.title)}
						/>
						<FooterCol
							links={[
								t(($) => $.modules.marketing.footer.columns.company.links.l1),
								t(($) => $.modules.marketing.footer.columns.company.links.l2),
								t(($) => $.modules.marketing.footer.columns.company.links.l3),
								t(($) => $.modules.marketing.footer.columns.company.links.l4),
							]}
							title={t(($) => $.modules.marketing.footer.columns.company.title)}
						/>
						<FooterCol
							links={[
								t(($) => $.modules.marketing.footer.columns.resources.links.l1),
								t(($) => $.modules.marketing.footer.columns.resources.links.l2),
								t(($) => $.modules.marketing.footer.columns.resources.links.l3),
							]}
							title={t(($) => $.modules.marketing.footer.columns.resources.title)}
						/>
					</div>
					<div className="flex flex-wrap justify-between gap-3 border-stone-200 border-t pt-6 dark:border-white/10">
						<span>© {new Date().getFullYear()} OdontOffice</span>
						<span>{t(($) => $.modules.marketing.footer.legal)}</span>
					</div>
				</div>
			</footer>
		</div>
	);
}

function FooterCol({ title, links }: { title: string; links: Array<string> }) {
	return (
		<div>
			<h4 className="mb-3.5 font-semibold text-[13px] text-brand-brown uppercase tracking-wider dark:text-stone-100">
				{title}
			</h4>
			<ul className="grid gap-2.5">
				{links.map((label) => (
					<li key={label}>
						<Link className="transition-colors hover:text-brand-brown dark:hover:text-stone-100" to="/">
							{label}
						</Link>
					</li>
				))}
			</ul>
		</div>
	);
}
